/**
 * Shared display metadata for the Broker Accounts page (Broker Integration
 * — Phase 1). One entry per `BrokerName` — adding a 4th broker later means
 * adding one entry here (plus the backend's own adapter + factory branch,
 * see `app.integrations.brokers.factory`), no other frontend file needs to
 * change.
 *
 * Card avatar colors below are this app's own UI palette, not an attempt to
 * reproduce any broker's real brand colors/logo — TekInvest has no licensed
 * broker logo assets, so each card uses a plain colored initial instead
 * (see `BrokerAccountCard`).
 */
import type { BrokerApiHealth, BrokerConnectionStatus, BrokerName } from "@/types";

export interface BrokerMeta {
  name: BrokerName;
  displayName: string;
  shortLabel: string;
  avatarColor: string;
  /** Optional one-line note shown in the Connect dialog above the login button. */
  connectNote?: string;
}

export const BROKER_META: Record<BrokerName, BrokerMeta> = {
  DHAN: { name: "DHAN", displayName: "Dhan", shortLabel: "D", avatarColor: "#0F3D63" },
  ZERODHA: { name: "ZERODHA", displayName: "Zerodha (Kite Connect)", shortLabel: "Z", avatarColor: "#387ED1" },
  ICICI_BREEZE: { name: "ICICI_BREEZE", displayName: "ICICI Direct (Breeze)", shortLabel: "I", avatarColor: "#B8323C" },
  GROWW: { name: "GROWW", displayName: "Groww", shortLabel: "G", avatarColor: "#00D09C" },
  ANGEL_ONE: {
    name: "ANGEL_ONE",
    displayName: "Angel One",
    shortLabel: "A",
    avatarColor: "#E85D2F",
    connectNote:
      "You will sign in on Angel One's own page (client ID, PIN and TOTP) — TekInvest never sees them. " +
      "The session ends at midnight IST, so reconnect each trading day.",
  },
};

/** Display order for the cards — stable regardless of API response order. */
export const BROKER_ORDER: BrokerName[] = ["DHAN", "ZERODHA", "ICICI_BREEZE", "GROWW", "ANGEL_ONE"];

export const CONNECTION_STATUS_META: Record<
  BrokerConnectionStatus,
  { label: string; dot: string; color: "success" | "warning" | "error" }
> = {
  CONNECTED: { label: "Connected", dot: "\u{1F7E2}", color: "success" },
  SESSION_EXPIRING: { label: "Session Expiring", dot: "\u{1F7E1}", color: "warning" },
  DISCONNECTED: { label: "Disconnected", dot: "\u{1F534}", color: "error" },
};

export const API_HEALTH_COLOR: Record<BrokerApiHealth, "success" | "error" | "default"> = {
  HEALTHY: "success",
  UNHEALTHY: "error",
  UNKNOWN: "default",
};

/** Friendly labels for the manual-paste credential fields each adapter's
 * `get_login_info().manual_paste_fields` may return — see backend
 * `app.integrations.brokers.*_adapter.get_login_info` for the authoritative
 * per-broker field list. Falls back to the raw field name (title-cased) for
 * any key not listed here, so a future broker never renders blank. */
export const CREDENTIAL_FIELD_LABELS: Record<string, string> = {
  client_id: "Client ID",
  access_token: "Access Token",
  session_token: "Session Token (apisession)",
  account_name: "Account Name",
};

export function credentialFieldLabel(field: string): string {
  return CREDENTIAL_FIELD_LABELS[field] ?? field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN");
}
